// Shared GDPR helpers used by both backend and web-nuxt Sentry init.
// Keep this module dependency-free (no Node, no DOM, no Sentry types) so it
// stays usable on both runtimes.
/**
 * Field names that must never reach a US sub-processor (Sentry, PostHog, …).
 *
 * Matched case-insensitively against object keys. New aliases (`Email`,
 * `EMAIL`, `phoneNumber`, `phone_number`, `givenName`, `personnummer`, etc.)
 * are caught automatically because lookup normalises to lowercase.
 */
export const PII_KEYS = new Set([
    "email",
    "phone",
    "phonenumber",
    "personalnumber",
    "personnummer",
    "ssn",
    "firstname",
    "lastname",
    "givenname",
    "familyname",
    "fullname",
    "authuserid",
    "password",
    "token",
    "accesstoken",
    "refreshtoken",
    "sessionid",
]);
const REDACTED = "[redacted]";
// Class-like values we must pass through unchanged: rebuilding them with
// Object.entries strips non-enumerable fields (Error.message, Error.stack),
// loses prototype methods (Date.toJSON, Buffer.toString), and corrupts
// downstream consumers that expect the real instance.
const isPassthroughInstance = (v) => v instanceof Error ||
    v instanceof Date ||
    v instanceof RegExp ||
    v instanceof Map ||
    v instanceof Set ||
    v instanceof Promise ||
    // Node Buffer + browser ArrayBuffer/typed arrays — keep raw.
    (typeof ArrayBuffer !== "undefined" && ArrayBuffer.isView(v)) ||
    (typeof globalThis.Buffer
        !== "undefined"
        && Boolean(globalThis
            .Buffer?.isBuffer?.(v)));
/**
 * Recursively replace PII-keyed fields with "[redacted]".
 *
 * Cycle-safe (WeakSet seen-guard) and instance-safe (Error/Date/Buffer/typed
 * arrays/Map/Set pass through unchanged). On internal failure (e.g. a throwing
 * getter or hostile Proxy in the payload), returns the redaction placeholder
 * rather than the original input — better to lose debugging context than to
 * silently leak PII when scrubbing fails.
 */
export const scrubPii = (value) => {
    const seen = new WeakSet();
    const walk = (v) => {
        if (v === null || typeof v !== "object")
            return v;
        // Passthrough check must precede the WeakSet add: otherwise the same Error
        // appearing twice in a payload becomes "[cycle]" instead of the instance.
        if (isPassthroughInstance(v))
            return v;
        if (seen.has(v))
            return "[cycle]";
        seen.add(v);
        if (Array.isArray(v))
            return v.map(walk);
        const out = {};
        for (const [k, vv] of Object.entries(v)) {
            out[k] = PII_KEYS.has(k.toLowerCase()) ? REDACTED : walk(vv);
        }
        return out;
    };
    try {
        return walk(value);
    }
    catch {
        // Fail closed: don't ship the raw payload. Downstream consumers see a
        // placeholder string instead of a structured object on this branch.
        return REDACTED;
    }
};
